import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPinOff, ArrowRight, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Nov 2026 (SCWT-34E6D6) — Posted-kit address repair tile.
 * Fetches `/api/admin/posted-kit/needing-address-repair` and shows the
 * exact count of paid Mail-Out Kit bookings that can't get a shipping
 * label yet (no reviewed delivery address on file). Clicking the tile
 * lands on the full list at `/admin/posted-kit/needs-address`.
 */
export default function PostedKitAddressRepairTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/admin/posted-kit/needing-address-repair")
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const count = data?.count ?? 0;
  const needsAttention = count > 0;
  const wrapClass = needsAttention
    ? "border-amber-300 bg-gradient-to-br from-amber-50 to-white"
    : "border-slate-200 bg-white";

  return (
    <Link
      to="/admin/posted-kit/needs-address"
      data-testid="kpi-tile-posted-kit-address-repair"
      className={`group relative rounded-2xl border ${wrapClass} p-5 hover:shadow-md transition`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPinOff className="w-4 h-4 text-slate-500" />
          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500">
            Mail-Out Kits — Address Needed
          </div>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:translate-x-0.5 transition" />
      </div>
      <div className="mt-3" data-testid="posted-kit-address-repair-count">
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        ) : (
          <div className={`text-3xl font-bold ${needsAttention ? "text-amber-700" : "text-slate-800"}`}>
            {count}
          </div>
        )}
      </div>
      <div className="mt-1 text-[12px] text-slate-500">
        {needsAttention
          ? "paid bookings can't get a shipping label until the delivery address is confirmed"
          : "every paid Mail-Out Kit booking has a confirmed delivery address"}
      </div>
    </Link>
  );
}
